import { useCallback, useEffect } from "react";

import { FormInstance } from "antd/es/form";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import {
  EntityTypesSupportingFieldCustomization,
  Group,
} from "@mapmycustomers/shared/types/entity";
import DealLossReason from "@mapmycustomers/shared/types/entity/deals/DealLossReason";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { ChildLayout, LayoutSchemaField } from "@mapmycustomers/shared/types/layout/FormLayout";

import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import { parseApiDate } from "@app/util/parsers";

const useLayoutFields = (
  form: FormInstance<any>,
  activeSchema: LayoutSchemaField[],
  entityType: EntityTypesSupportingFieldCustomization,
  childLayout: ChildLayout | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>
) => {
  const updateLayoutFields = useCallback(
    (schemaField: LayoutSchemaField, field: IField) => {
      if (schemaField.fieldType === SchemaFieldType.GROUP) {
        form.setFieldValue(
          field.name,
          schemaField.defaultValue?.length
            ? new Set(schemaField.defaultValue as Group["id"][])
            : undefined
        );
      } else if (field.name === DealFieldName.DEAL_LOSS_REASON) {
        form.setFieldValue(
          ["default", schemaField.field],
          schemaField.defaultValue?.[0] as DealLossReason["id"] | undefined
        );
      } else if (field.isDefaultValueAllowed) {
        if (isCustomField(field)) {
          const definition = field.customFieldData;
          form.setFieldValue(["customFields", String(definition?.id)], {
            customField: { id: definition?.id },
            value: schemaField.defaultValue,
          });
        } else if (field.type === FieldType.NUMBER) {
          form.setFieldValue(
            ["default", schemaField.field],
            schemaField.defaultValue?.[0] as number | undefined
          );
        } else if (field.type === FieldType.STRING) {
          form.setFieldValue(
            ["default", schemaField.field],
            schemaField.defaultValue?.[0] as string | undefined
          );
        } else if (field.type === FieldType.DATE_TIME) {
          form.setFieldValue(
            ["default", schemaField.field],
            schemaField.defaultValue?.length && parseApiDate(schemaField.defaultValue[0] as string)
          );
        }
      }
    },
    [form]
  );

  useEffect(() => {
    if (childLayout) {
      if (childLayout.crmActivityType) {
        form.setFieldValue(ActivityFieldName.ACTIVITY_TYPE, childLayout.crmActivityType.id);
      } else if (childLayout.funnel && childLayout.stage) {
        form.setFieldValue(DealFieldName.FUNNEL, childLayout.funnel.id);
        form.setFieldValue(DealFieldName.STAGE, childLayout.stage.id);
      }

      childLayout.schema.forEach((schemaField) => {
        const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);

        if (!field) {
          return;
        }

        updateLayoutFields(schemaField, field);
      });
    } else {
      activeSchema.forEach((schemaField) => {
        const field = getFieldModelByEntityType(entityType).getByPlatformName(schemaField.field);

        if (!field) {
          return;
        }

        if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD)) {
          form.setFieldValue(ActivityFieldName.ACTIVITY_TYPE, schemaField.defaultValue?.[0]);
        }

        if (field.hasFeature(FieldFeature.STAGE_FIELD)) {
          const stageDefaultValue = schemaField.defaultValue?.[0] as Stage["id"];
          if (stageDefaultValue) {
            const funnelIdString = Object.keys(funnelStages).find((funnelId) =>
              funnelStages[parseInt(funnelId) as Funnel["id"]].some(
                ({ id }) => id === stageDefaultValue
              )
            );
            if (funnelIdString) {
              form.setFieldValue(DealFieldName.STAGE, stageDefaultValue);
              form.setFieldValue(DealFieldName.FUNNEL, parseInt(funnelIdString));
            }
          } else {
            form.setFieldValue(DealFieldName.STAGE, undefined);
          }
        }

        if (field.hasFeature(FieldFeature.FUNNEL_FIELD)) {
          const funnelDefaultValue = schemaField.defaultValue?.[0] as number;
          if (funnelDefaultValue) {
            form.setFieldValue(DealFieldName.FUNNEL, funnelDefaultValue);
            form.setFieldValue(DealFieldName.STAGE, undefined);
          } else {
            form.setFieldValue(DealFieldName.FUNNEL, undefined);
          }
        }

        updateLayoutFields(schemaField, field);
      });
    }
  }, [activeSchema, childLayout, entityType, form, funnelStages, updateLayoutFields]);
};

export default useLayoutFields;
